import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { attributeDefinitionIdFormat } from "../../ids";
import { AttributeType, AttributeValueSchema } from "../attributes";
import { BaseProductListingSchema, ListingType } from "../base-listing";
import {
  GoogleShoppingAgeGroupSchema,
  GoogleShoppingAvailabilitySchema,
  GoogleShoppingConditionSchema,
  GoogleShoppingGenderSchema,
  GoogleShoppingPriceSchema,
  GoogleShoppingShippingDimensionSchema,
  GoogleShoppingShippingWeightSchema,
} from "./common-schemas";
import {
  GoogleShoppingListingMediaSchema,
  GoogleShoppingListingMediaWithGoogleIds,
  GoogleShoppingListingMediaWithoutRedoIds,
} from "./google-shopping-media";

/**
 * Google Shopping variant field types for attribute mapping
 * Matches the SQL enum: gshop_variant_field_type
 */
export enum GoogleShoppingVariantFieldType {
  offer_id = "offer_id",
  price = "price",
  sale_price = "sale_price",
  availability = "availability",
  condition = "condition",
  gtin = "gtin",
  mpn = "mpn",
  shipping_weight = "shipping_weight",
  shipping_length = "shipping_length",
  shipping_width = "shipping_width",
  shipping_height = "shipping_height",
  color = "color",
  size = "size",
  material = "material",
  pattern = "pattern",
  age_group = "age_group",
  gender = "gender",
}
export const GoogleShoppingVariantFieldTypeSchema = createEnum(
  GoogleShoppingVariantFieldType,
);

export const GoogleShoppingVariantListingSchema_ =
  BaseProductListingSchema.extend({
    listingType: z.literal(ListingType.GoogleShopping),
    id: z.bigint(),
    offerId: z.string(),
    price: GoogleShoppingPriceSchema,
    salePrice: GoogleShoppingPriceSchema.nullable(),
    availability: GoogleShoppingAvailabilitySchema,
    condition: GoogleShoppingConditionSchema.nullable(),
    gtin: z.string().nullable(),
    mpn: z.string().nullable(),
    shippingWeight: GoogleShoppingShippingWeightSchema.nullable(),
    shippingLength: GoogleShoppingShippingDimensionSchema.nullable(),
    shippingWidth: GoogleShoppingShippingDimensionSchema.nullable(),
    shippingHeight: GoogleShoppingShippingDimensionSchema.nullable(),
    color: z.string().nullable(),
    size: z.string().nullable(),
    material: z.string().nullable(),
    pattern: z.string().nullable(),
    ageGroup: GoogleShoppingAgeGroupSchema.nullable(),
    gender: GoogleShoppingGenderSchema.nullable(),
    media: z.array(GoogleShoppingListingMediaSchema),
    createdAt: z.date(),
    updatedAt: z.date(),
  });
export const GoogleShoppingVariantListingSchema: z.ZodType<GoogleShoppingVariantListing> =
  GoogleShoppingVariantListingSchema_;
export interface GoogleShoppingVariantListing
  extends z.infer<typeof GoogleShoppingVariantListingSchema_> {}

export type GoogleShoppingVariantListingWithoutRedoIds = Omit<
  GoogleShoppingVariantListing,
  "id" | "productId" | "media"
> & { media: GoogleShoppingListingMediaWithoutRedoIds[] };

export type GoogleShoppingVariantListingWithGoogleIds = Omit<
  GoogleShoppingVariantListing,
  "media"
> & { media: GoogleShoppingListingMediaWithGoogleIds[] };

const GoogleShoppingVariantMappingSchema_ = z.object({
  fieldName: GoogleShoppingVariantFieldTypeSchema,
  attributeDefinitionId: attributeDefinitionIdFormat.schema,
});
export const GoogleShoppingVariantMappingSchema: z.ZodType<GoogleShoppingVariantMapping> =
  GoogleShoppingVariantMappingSchema_;
export interface GoogleShoppingVariantMapping
  extends z.infer<typeof GoogleShoppingVariantMappingSchema_> {}

export const GoogleShoppingVariantAttributeType = {
  [GoogleShoppingVariantFieldType.offer_id]: AttributeType.ShortText,
  [GoogleShoppingVariantFieldType.price]: AttributeType.Money,
  [GoogleShoppingVariantFieldType.sale_price]: AttributeType.Money,
  [GoogleShoppingVariantFieldType.availability]: AttributeType.Enum,
  [GoogleShoppingVariantFieldType.condition]: AttributeType.Enum,
  [GoogleShoppingVariantFieldType.gtin]: AttributeType.ShortText,
  [GoogleShoppingVariantFieldType.mpn]: AttributeType.ShortText,
  [GoogleShoppingVariantFieldType.shipping_weight]: AttributeType.Weight,
  [GoogleShoppingVariantFieldType.shipping_length]: AttributeType.Float,
  [GoogleShoppingVariantFieldType.shipping_width]: AttributeType.Float,
  [GoogleShoppingVariantFieldType.shipping_height]: AttributeType.Float,
  [GoogleShoppingVariantFieldType.color]: AttributeType.ShortText,
  [GoogleShoppingVariantFieldType.size]: AttributeType.ShortText,
  [GoogleShoppingVariantFieldType.material]: AttributeType.ShortText,
  [GoogleShoppingVariantFieldType.pattern]: AttributeType.ShortText,
  [GoogleShoppingVariantFieldType.age_group]: AttributeType.Enum,
  [GoogleShoppingVariantFieldType.gender]: AttributeType.Enum,
} satisfies Record<GoogleShoppingVariantFieldType, AttributeType>;

export const GoogleShoppingVariantAttributeSchema = {
  [GoogleShoppingVariantFieldType.offer_id]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[
        GoogleShoppingVariantFieldType.offer_id
      ]
    ].nullable(),
  [GoogleShoppingVariantFieldType.price]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GoogleShoppingVariantFieldType.price]
    ].nullable(),
  [GoogleShoppingVariantFieldType.sale_price]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[
        GoogleShoppingVariantFieldType.sale_price
      ]
    ].nullable(),
  [GoogleShoppingVariantFieldType.availability]:
    GoogleShoppingAvailabilitySchema.nullable(),
  [GoogleShoppingVariantFieldType.condition]:
    GoogleShoppingConditionSchema.nullable(),
  [GoogleShoppingVariantFieldType.gtin]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GoogleShoppingVariantFieldType.gtin]
    ].nullable(),
  [GoogleShoppingVariantFieldType.mpn]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GoogleShoppingVariantFieldType.mpn]
    ].nullable(),
  [GoogleShoppingVariantFieldType.shipping_weight]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[
        GoogleShoppingVariantFieldType.shipping_weight
      ]
    ].nullable(),
  [GoogleShoppingVariantFieldType.shipping_length]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[
        GoogleShoppingVariantFieldType.shipping_length
      ]
    ].nullable(),
  [GoogleShoppingVariantFieldType.shipping_width]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[
        GoogleShoppingVariantFieldType.shipping_width
      ]
    ].nullable(),
  [GoogleShoppingVariantFieldType.shipping_height]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[
        GoogleShoppingVariantFieldType.shipping_height
      ]
    ].nullable(),
  [GoogleShoppingVariantFieldType.color]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GoogleShoppingVariantFieldType.color]
    ].nullable(),
  [GoogleShoppingVariantFieldType.size]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GoogleShoppingVariantFieldType.size]
    ].nullable(),
  [GoogleShoppingVariantFieldType.material]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[
        GoogleShoppingVariantFieldType.material
      ]
    ].nullable(),
  [GoogleShoppingVariantFieldType.pattern]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GoogleShoppingVariantFieldType.pattern]
    ].nullable(),
  [GoogleShoppingVariantFieldType.age_group]:
    GoogleShoppingAgeGroupSchema.nullable(),
  [GoogleShoppingVariantFieldType.gender]:
    GoogleShoppingGenderSchema.nullable(),
};

export type GoogleShoppingVariantAttributeValueType = {
  [key in GoogleShoppingVariantFieldType]: (typeof GoogleShoppingVariantAttributeSchema)[key] extends z.ZodType<
    infer T
  >
    ? T
    : never;
};

export function getGoogleShoppingVariantAttributeValue<
  T extends GoogleShoppingVariantFieldType,
>(field: T, value: unknown): GoogleShoppingVariantAttributeValueType[T] | null {
  return GoogleShoppingVariantAttributeSchema[field].parse(
    value,
  ) as GoogleShoppingVariantAttributeValueType[T];
}

export function getGoogleShoppingVariantAttributeType<
  T extends GoogleShoppingVariantFieldType,
>(field: T): (typeof GoogleShoppingVariantAttributeType)[T] {
  return GoogleShoppingVariantAttributeType[field];
}
